import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'reactstrap';
import { withTranslation } from 'react-i18next';
import { getDeliverySettings } from '../../../API/PhishingAPI';
import PageTitle from '../../../Layout/Components/PageTitle';
import PhishingIcon from '../../../Layout/Components/PhishingIcon';
import './DeliverySettings.css';
import AuthenticatedDomains from './AuthenticatedDomains';

export function DeliverySettingsPanel({ t }) {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const request = useRef(0);
  const load = useCallback(async () => {
    const current = ++request.current;
    setBusy(true); setError(false);
    try {
      const result = await getDeliverySettings();
      if (!Array.isArray(result?.sendingServers) || !Array.isArray(result?.linkDomains)) throw new Error('Invalid delivery settings');
      if (request.current === current) setSettings(result);
    } catch (_) {
      if (request.current === current) setError(true);
    } finally { if (request.current === current) setBusy(false); }
  }, []);
  useEffect(() => { load(); return () => { ++request.current; }; }, [load]);
  return <div className="delivery-settings">
    <ol className="delivery-settings__steps" aria-label={t('delivery_settings.setup_steps')}>
      {['verify_step', 'gateway_step', 'test_step'].map((step, index) => <li key={step}><span aria-hidden="true">{index + 1}</span>{t(`delivery_settings.${step}`)}</li>)}
    </ol>
    <AuthenticatedDomains t={t} />
    <section className="delivery-settings__section" aria-labelledby="delivery-gateway-heading">
      <h2 id="delivery-gateway-heading"><span className="delivery-settings__step-number" aria-hidden="true">2</span>{t('delivery_settings.gateway_title')}</h2>
      <p>{t('delivery_settings.gateway_description')}</p>
      {error && <Alert color="danger" role="alert">{t('delivery_settings.load_error')}{' '}
        <Button color="link" disabled={busy} onClick={load}>{t('template_gallery.retry')}</Button></Alert>}
      {!settings && busy && <p role="status">{t('simulation_wizard.loading')}</p>}
      {settings && <>
        <h3 className="delivery-settings__subheading">{t('delivery_settings.servers_title')}</h3>
        {settings.sendingServers.length ? <div className="delivery-settings__table"><table><thead><tr>
          <th>{t('delivery_settings.address')}</th><th>{t('delivery_settings.region')}</th></tr></thead>
          <tbody>{settings.sendingServers.map(server => <tr key={server.address}><td><code>{server.address}</code></td><td>{server.region || '—'}</td></tr>)}</tbody>
        </table></div> : <p className="delivery-settings__pending">{t('delivery_settings.servers_pending')}</p>}
        <p className="delivery-settings__note">{t('delivery_settings.mailfrom_help')}</p>
        {settings.sendingDomain && <p className="delivery-settings__technical-domain"><strong>{t('delivery_settings.mailfrom_label')}: </strong><code>{settings.sendingDomain}</code></p>}
        <div className="delivery-settings__guides">
          <a href="/organization/phishing_simulator/delivery-settings/microsoft-365">Microsoft 365 — {t('delivery_settings.microsoft_guide')}</a>
          <a href="https://knowledge.workspace.google.com/admin/gmail/advanced/add-ip-addresses-to-allowlists-in-gmail" target="_blank" rel="noopener noreferrer">Google Workspace — {t('delivery_settings.google_allowlist_guide')} ↗</a>
        </div>
        <details className="delivery-settings__link-domains"><summary>{t('campaign_link_domains.title')}</summary>
          <p>{t('campaign_link_domains.managed_help')}</p>
          {settings.linkDomains.length ? <div className="delivery-settings__table"><table><thead><tr>
            <th>{t('campaign_link_domains.name')}</th><th>{t('campaign_link_domains.address')}</th><th>{t('campaign_link_domains.status')}</th>
          </tr></thead><tbody>{settings.linkDomains.map(domain => <tr key={domain.id}>
            <td>{domain.label}{domain.id === settings.defaultLinkDomainId && <span className="delivery-settings__domain-default">{t('campaign_link_domains.default')}</span>}</td>
            <td><code>{domain.url}</code></td><td><span className={`delivery-settings__status${domain.enabled ? ' delivery-settings__status--published' : ''}`}>
              {t(domain.enabled ? 'campaign_link_domains.available' : 'campaign_link_domains.pending')}</span></td>
          </tr>)}</tbody></table></div> : <p className="delivery-settings__pending">{t('campaign_link_domains.none_registered')}</p>}
        </details>
      </>}
    </section>
    <section className="delivery-settings__section" aria-labelledby="delivery-test-heading">
      <h2 id="delivery-test-heading"><span className="delivery-settings__step-number" aria-hidden="true">3</span>{t('delivery_settings.test_title')}</h2>
      <p>{t('delivery_settings.test_description')}</p>
      <p className="delivery-settings__note">{t('delivery_settings.txt_scope')}</p>
    </section>
  </div>;
}

function DeliverySettings({ t }) {
  return <><PageTitle heading={t('delivery_settings.title')} subheading={t('delivery_settings.subtitle')} icon={<PhishingIcon name="delivery" />} />
    <div className="delivery-settings-page"><DeliverySettingsPanel t={t} /></div></>;
}
export default withTranslation()(DeliverySettings);
